/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 */
import React from 'react';
import { InputAdornment } from '@mui/material';
import CustomModal from '../../../components/Modal/CustomModal';
import { CustomTextField } from '../../../components/CustomTextField';
import { CustomAddChip } from '../../../components/CustomAddChip';
import { EmailSubsectionChip } from '../../../components/buttons/IconButtons';
import { isEmailFormat } from '../../../Apis/functions';
import { CreateHouseholdMemberModalApp } from '../Functionality/CreateHouseholdMemberModalApp';

/**
 * Form the logged resident fills to add a member to their household, opened from
 * the add member button next to the tabs of the building detail.
 *
 * @param {Object} props
 * @param {boolean} props.show - Renders the modal.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Function} props.setModified - Flags the members list to be reloaded after a successful creation.
 * @param {Object} props.buildingDetail - Building of the logged resident, the member is registered on it.
 */
const CreateHouseholdMemberModal = ({ show, onHide, setModified, buildingDetail }) => {
    const {
        t,
        emptyFields,
        member,
        relationshipList,
        relationshipOptions,
        clearButton,
        handleChange,
        handleSelectRelationship,
        handleClose,
        handleSave
    } = CreateHouseholdMemberModalApp({ show, onHide, setModified, buildingDetail });

    return (
        <CustomModal
            title={'add_household_member'}
            rightTitleButton={clearButton()}
            show={show}
            size={'m'}
            buttonConfirm={'confirm'}
            buttonCancelText={'cancel'}
            cancelButton={true}
            confirmButton={true}
            disabled={false}
            onConfirm={handleSave}
            onHide={handleClose}
            classNameBody={'pt-2'}
        >
            <div className={'row'}>
                <div className={'col-12'}>
                    <CustomTextField
                        id={'txt_chm_name'}
                        length={128}
                        name={'name'}
                        value={member?.name}
                        trans={'name'}
                        type={'text'}
                        disabled={false}
                        onChange={handleChange}
                        required={true}
                        emptyFields={emptyFields}
                        placeholder={t('enter_name')}
                    />
                </div>

                <div className={'col-12'}>
                    <CustomTextField
                        id={'txt_chm_last_name'}
                        length={128}
                        name={'lastName'}
                        value={member?.lastName}
                        trans={'last_name'}
                        type={'text'}
                        disabled={false}
                        onChange={handleChange}
                        required={true}
                        emptyFields={emptyFields}
                        placeholder={t('enter_last_name')}
                    />
                </div>

                <div className={'col-12'}>
                    <CustomTextField
                        id={'txt_chm_email'}
                        length={128}
                        name={'email'}
                        value={member?.email}
                        trans={'email'}
                        type={'text'}
                        disabled={false}
                        onChange={handleChange}
                        required={true}
                        emptyFields={emptyFields}
                        isValid={(!member?.email || isEmailFormat(member?.email))}
                        isValidText={'email_format_invalid'}
                        placeholder={t('enter_email')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <EmailSubsectionChip className={'w-h-24px'} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>

                <div className={'col-12 mt-2'}>
                    <CustomAddChip
                        autocompleteId={'slt_chm_relationship'}
                        buttonId={'btn_chm_relationship'}
                        data={relationshipOptions}
                        label={'relationship'}
                        selectedList={relationshipList}
                        setSelectedList={handleSelectRelationship}
                        required={true}
                        disabled={false}
                        emptyFields={emptyFields && relationshipList.length === 0}
                        maxLength={1}
                        hideButton={true}
                        reset={false}
                    />
                </div>
            </div>
        </CustomModal>
    );
};

export default CreateHouseholdMemberModal;
